/**
 * **Deploy placement legality** — the structural-invariant
 * gate `deploy_drone`/`deploy_mine` share, mirroring `pilotBridge.ts`'s `checkDismountLegality`
 * shape exactly (adjacency + a free-footprint fit via the SAME `footprintEndpointFits` +
 * `movementContext` seam), generalized to any Size so both a Size-½ drone and a Size-1 mine
 * ride ONE placement primitive rather than two near-duplicates. Mines carry one EXTRA rule
 * (Core p.106: "not adjacent to any other mines") layered on top via {@link
 * checkMineAdjacency} — a separate, composable check, not folded into the generic one (a
 * drone deploy never runs it).
 *
 * This is the **structural-invariant tier**: even a hypothetical force-apply override could
 * never place a mine overlapping another mine or off the free-cell rule — and there is no
 * force-deploy command at all (deploy has no "GM override" sibling, unlike move/attack), so in
 * practice this is simply always enforced, but the check is written the same pure-predicate way
 * every other structural gate is, for consistency.
 */

import type { Cell, CombatantId, EncounterState, World } from "@lancer-vtt/domain";
import { gridExtent, placementFits, terrainVisibility, worldFor } from "@lancer-vtt/domain";
import { type Result, ok, err } from "@lancer-vtt/shared";
import { combatantCellRange, footprintCells, footprintDistance } from "./effects/spatialAnswerer";

/** A deploy-placement verdict — `Result<void>`, mirroring `pilotBridge.ts`'s `BridgeVerdict`. */
export type DeployVerdict = Result<void>;

/**
 * Does a `size`-footprint fit, unoccupied and off solid terrain, anchored at `at`? Uses
 * `movementContext`'s missing-mover fallback (a synthetic id no live combatant can resolve to)
 * — the correct "brand-new spawn, nothing to exempt" semantics {@link
 * "./pilotBridge".pilotFootprintFits} already established for Dismount/Eject.
 */
export function deployCellFits(state: EncounterState, at: Cell, size: number): boolean {
  return placementFits(state, { mover: "__deploy_spawn__", size, anchor: at });
}

/** Named arguments for {@link checkDeployPlacement} (`max-params`-3 discipline). */
export type CheckDeployPlacementArgs = {
  state: EncounterState;
  deployerId: CombatantId;
  at: Cell;
  /** The footprint size of the entity being deployed (0.5 for a drone, 1 for a mine). */
  size: number;
}

/**
 * May `deployerId` deploy a `size`-footprint entity to `at` (DRONE/MINE tags, Core Book
 * pp.105–106: "an adjacent, free and valid space")? Checks, in order: the deployer exists and
 * is placed; `at` is exactly adjacent (distance 1) to the deployer's footprint; `at` has a free
 * fit (occupied/terrain-solid cells reject). Shared by both `deploy_drone` and `deploy_mine` —
 * the mine's EXTRA "not adjacent to another mine" rule is a separate check, {@link
 * checkMineAdjacency}, composed alongside this one by the room.
 */
export function checkDeployPlacement(args: CheckDeployPlacementArgs): DeployVerdict {
  const { state, deployerId, at, size } = args;
  const deployer = state.combatants[deployerId];
  if (!deployer) return err(`deploy: no such combatant ${deployerId}`);
  if (!deployer.pos) return err(`deploy: ${deployer.name} isn't placed on the map`);
  const dist = combatantCellRange(state, deployerId, at);
  if (dist !== 1) return err(`deploy: target must be an adjacent free space (got distance ${dist ?? "unreachable"})`);
  if (!deployCellFits(state, at, size)) return err(`deploy: no free space at that cell`);
  return ok(undefined);
}

/**
 * The cells adjacent to a footprint's outer boundary — the union of `world.neighbors` over
 * every cell the footprint occupies, minus the footprint's own cells. For a Size-1 anchor
 * (every real `deploy_object` carrier today — Jericho's sections, the turret drone) this is
 * identical to plain `world.neighbors(anchor)`; the footprint union generalizes correctly to
 * a larger deployer or section without a separate code path. Grid-agnostic — routes entirely
 * through `world.neighbors`/`world.key`, no hardcoded offsets, so it works unchanged on both
 * the 6-neighbour hex grid and the 8-neighbour square grid.
 */
function footprintNeighbors(world: World<Cell>, footprint: Cell[]): Cell[] {
  const footprintKeys = new Set(footprint.map((c) => world.key(c)));
  const seen = new Set<string>();
  const out: Cell[] = [];
  for (const cell of footprint) {
    for (const n of world.neighbors(cell)) {
      const k = world.key(n);
      if (footprintKeys.has(k) || seen.has(k)) continue;
      seen.add(k);
      out.push(n);
    }
  }
  return out;
}

/** Named arguments for {@link legalDirectedDeployCells} (`max-params`-3 discipline). */
export type LegalDirectedDeployCellsArgs = {
  state: EncounterState;
  deployerId: CombatantId;
  /** The footprint size of each section being placed (0.5 for the turret drone, 1 for Jericho). */
  size: number;
  /** Sections already picked, in pick order — the prefix this enumeration continues from. */
  placed: Cell[];
  /**
   * How many MORE picks are required **after** the one this call is enumerating candidates
   * for — `0` on the last pick (the turret's only pick, or Jericho's second section), `1` on
   * Jericho's first pick. This is the lookahead filter's knob: the enumeration has no other
   * way to know whether the cell it is about to offer is a dead end, because "is there a legal
   * next pick from here" depends on how many picks are still owed, not on anything in `state`
   * or `placed` alone. With `remaining > 0`, a candidate is offered only if choosing it still
   * leaves at least one legal continuation — checked by recursing one level
   * (`legalDirectedDeployCells` again, with that candidate appended to `placed` and `remaining
   * - 1`). This composes correctly for `remaining` > 1 by induction: the recursive call's own
   * returned set already excludes ITS dead ends, so "non-empty" at each level certifies a full
   * continuable chain, not just a one-step peek. A `remaining` of `0` (the turret's single pick)
   * runs no lookahead at all — a 1-pick deploy has nothing to look ahead to.
   */
  remaining: number;
  /**
   * True to ALSO exclude any candidate cell that fails {@link checkMineAdjacency}'s "not
   * adjacent to any other mines" wall — the mine-only addition to the generic adjacent-free-fit
   * rule every directed deploy shares. **This is the ONE shared derivation the browser wash and
   * the room gate both key on** so they can never independently disagree about which cells a
   * mine may land on (mirroring how `size`/`placed` already keep Jericho's wash and gate in
   * lockstep). Absent/`false` for every non-mine directed deploy (Jericho's two cover sections,
   * the turret drone) — those never run this wall.
   */
  mine?: boolean;
  /**
   * `"sensors"` swaps the adjacency pool for {@link legalSensorsDeployCells}' (the DRONE tag: "a
   * free, valid space within Sensors and line of sight"); `{ within: N }` does the same at a fixed
   * range (Javelin Rockets: "free spaces within range 15 and line of sight"). No chain rule between
   * picks: each pick is anywhere in that set that doesn't overlap an earlier one, and for a mine
   * isn't adjacent to one either ("that aren't adjacent to each other"). Absent → adjacency.
   */
  placement?: DeployPlacement;
};

/**
 * Where a directed deploy may land: `"sensors"` (the DRONE tag), `{ within: N }` (a fixed range),
 * `{ mutualWithin: N }` (any free space in line of sight, each pick within N of the earlier ones:
 * Accelerate's two marked spaces), or, absent, adjacent to the deployer.
 */
export type DeployPlacement = "sensors" | { within: number } | { mutualWithin: number };

/** Named arguments for {@link legalSensorsDeployCells} (`max-params`-3 discipline). */
export type LegalSensorsDeployCellsArgs = {
  state: EncounterState;
  deployerId: CombatantId;
  /** The footprint size of the deployed drone (0.5 for every LL1 drone). */
  size: number;
  /** A fixed range in place of the deployer's Sensors. */
  within?: number;
};

/**
 * The cells a drone may deploy to (`tg_drone`: "a free, valid space within Sensors and line of
 * sight"): every anchor whose `size` footprint fits free ({@link deployCellFits}), within the
 * deployer's frozen `base.sensors` of its footprint, with an unblocked sightline from the
 * deployer's footprint to the drone's. The ONE derivation the browser wash and the server wall
 * ({@link checkDirectedDeployPlacement} with `placement: "sensors"`) both read. Empty when the
 * deployer is unknown, unplaced or has no Sensors.
 */
export function legalSensorsDeployCells(args: LegalSensorsDeployCellsArgs): Cell[] {
  const { state, deployerId, size, within } = args;
  const deployer = state.combatants[deployerId];
  const sensors = within ?? deployer?.base.sensors ?? 0;
  if (!deployer?.pos || sensors <= 0) return [];
  const world = worldFor(state);
  const from = footprintCells(world, deployer);
  const reach = sensors + Math.ceil(deployer.base.size);
  return world
    .disk(deployer.pos, reach)
    .cells()
    .filter((c) => {
      const dist = footprintDistance(world, from, [c]);
      if (dist === null || dist < 1 || dist > sensors) return false;
      if (!deployCellFits(state, c, size)) return false;
      const to = world.footprint(c, size).cells();
      return terrainVisibility({ space: world, terrain: state.terrain, from, to, edges: state.edges, combatants: state.combatants }).visible;
    });
}

/**
 * The **legal cells** for the next pick of a directed multi-pick deploy
 * (`deploy_object`) — free-and-fitting (reusing {@link deployCellFits}, the SAME primitive
 * {@link checkDeployPlacement} uses, so "free" never re-derives), adjacent to the deploying
 * mech, and — once `placed` is non-empty — ALSO adjacent to the last section placed. This is
 * the browser wash's and the server wall's ({@link checkDirectedDeployPlacement}) ONE shared
 * derivation; the wall is defined in terms of this enumeration so the two can never drift.
 *
 * **The RAW reading is strict** (`ms_pattern_a_jericho_deployable_cover`: "Deploy two sections
 * of Size 1 hard cover in free spaces adjacent to you and to each other"): every cell must be
 * adjacent to the deployer, and every cell after the first must ALSO be adjacent to the
 * previous cell. Do not soften this into a mere *preference* for anchor-adjacency.
 *
 * Cells already in `placed` are excluded (a picked cell can't be picked again — the caller's
 * client-side draft, not `state`, tracks the in-progress picks, since the whole gesture stays
 * unsent until it's complete). Empty when the deployer is unknown or unplaced.
 *
 * **`mine: true`** additionally requires each candidate to
 * pass {@link checkMineAdjacency} — so a mine deploy's wash (and, via {@link
 * checkDirectedDeployPlacement}, its gate) never offers a cell adjacent to an existing mine.
 */
export function legalDirectedDeployCells(args: LegalDirectedDeployCellsArgs): Cell[] {
  const { state, deployerId, size, placed, remaining, mine, placement } = args;
  const deployer = state.combatants[deployerId];
  if (!deployer || !deployer.pos) return [];
  const world = worldFor(state);
  if (placement !== undefined && placement !== "sensors" && "mutualWithin" in placement) {
    // No range from the deployer, so the scan's reach is the whole board.
    const { width, height } = gridExtent(state);
    const placedKeys = new Set(placed.map((p) => world.key(p)));
    return legalSensorsDeployCells({ state, deployerId, size, within: width + height }).filter(
      (c) => !placedKeys.has(world.key(c)) && placed.every((p) => world.distance(p, c) <= placement.mutualWithin),
    );
  }
  if (placement !== undefined) {
    const taken = new Set(placed.flatMap((p) => world.footprint(p, size).cells().map((c) => world.key(c))));
    const within = placement === "sensors" ? undefined : placement.within;
    return legalSensorsDeployCells({ state, deployerId, size, ...(within !== undefined ? { within } : {}) }).filter(
      (c) =>
        world.footprint(c, size).cells().every((f) => !taken.has(world.key(f))) &&
        (!mine || (checkMineAdjacency(state, c).ok && placed.every((p) => world.distance(p, c) > 1))),
    );
  }
  const deployerNeighbors = footprintNeighbors(world, footprintCells(world, deployer));
  const placedKeys = new Set(placed.map((c) => world.key(c)));

  const last = placed[placed.length - 1];
  let pool: Cell[];
  if (last === undefined) {
    pool = deployerNeighbors;
  } else {
    const deployerNeighborKeys = new Set(deployerNeighbors.map((c) => world.key(c)));
    pool = footprintNeighbors(world, world.footprint(last, size).cells()).filter((c) => deployerNeighborKeys.has(world.key(c)));
  }

  const legal = pool.filter(
    (c) => !placedKeys.has(world.key(c)) && deployCellFits(state, c, size) && (!mine || checkMineAdjacency(state, c).ok),
  );
  if (remaining <= 0) return legal;

  // Lookahead: keep only candidates that leave at least one legal continuation.
  return legal.filter(
    (c) => legalDirectedDeployCells({ state, deployerId, size, placed: [...placed, c], remaining: remaining - 1, mine }).length > 0,
  );
}

/** Named arguments for {@link checkDirectedDeployPlacement} (`max-params`-3 discipline). */
export type CheckDirectedDeployPlacementArgs = {
  state: EncounterState;
  deployerId: CombatantId;
  /** The picked cells, in pick order. */
  cells: Cell[];
  size: number;
  /** See {@link LegalDirectedDeployCellsArgs.placement}. */
  placement?: DeployPlacement;
  /** See {@link LegalDirectedDeployCellsArgs.mine}. */
  mine?: boolean;
};

/**
 * May `deployerId` place `cells`, in pick order, as a directed multi-pick deploy?
 * Walks `cells`, checking each one against {@link legalDirectedDeployCells}'s enumeration for
 * the prefix placed so far — so this is "is this cell a member of the SAME set the wash
 * offers," never an independently re-derived rule. `cells` must be non-empty.
 */
export function checkDirectedDeployPlacement(args: CheckDirectedDeployPlacementArgs): DeployVerdict {
  const { state, deployerId, cells, size, placement, mine } = args;
  if (cells.length === 0) return err("deploy: no cells supplied");
  const world = worldFor(state);
  for (let i = 0; i < cells.length; i++) {
    const cell = cells[i]!;
    const placed = cells.slice(0, i);
    const remaining = cells.length - i - 1;
    const legal = legalDirectedDeployCells({ state, deployerId, size, placed, remaining, ...(placement ? { placement } : {}), ...(mine ? { mine } : {}) });
    const legalKeys = new Set(legal.map((c) => world.key(c)));
    if (!legalKeys.has(world.key(cell))) {
      const rule =
        placement === "sensors"
          ? " (a free space within Sensors and line of sight)"
          : placement === undefined
            ? ""
            : "mutualWithin" in placement
              ? ` (a free space in line of sight, within ${placement.mutualWithin} of the other picks)`
              : ` (a free space within ${placement.within} and line of sight)`;
      return err(`deploy: cell ${i + 1} of ${cells.length} (${world.key(cell)}) is not a legal placement${rule}`);
    }
  }
  return ok(undefined);
}

/**
 * Mine-only (Core p.106): `at` may not be within Range 1 of ANY other placed mine, armed or
 * not, concealed or not (the rule is about the mine's own space, not what a searcher can see).
 * Scans every live combatant carrying a `mineProfile` — sorted ids for a deterministic
 * "first blocking mine" report, mirroring `checkHideCover`'s sorted-scan discipline.
 */
export function checkMineAdjacency(state: EncounterState, at: Cell): DeployVerdict {
  const world = worldFor(state);
  for (const id of Object.keys(state.combatants).sort()) {
    const c = state.combatants[id]!;
    if (c.mineProfile === undefined || !c.pos) continue;
    const dist = footprintDistance(world, [at], footprintCells(world, c));
    if (dist !== null && dist <= 1) {
      return err(`deploy: too close to ${c.name} — a mine can't be placed adjacent to another mine`);
    }
  }
  return ok(undefined);
}

/** Named arguments for {@link legalAreaDeployCells} (`max-params`-3 discipline). */
export type LegalAreaDeployCellsArgs = {
  state: EncounterState;
  /** The placement area's cells (a Burst's `world.disk(...).cells()`). */
  area: readonly Cell[];
  /** The footprint size of each section being placed. */
  size: number;
  /** Sections already picked in this deploy, in pick order; their footprints are excluded. */
  placed: readonly Cell[];
};

/**
 * The **legal cells** for the next pick of an area-scoped deploy (`deploy_object` with
 * `within`, `npcf_earthshaker_shells_bombard`'s "two Size 1 segments of hard cover in free spaces
 * … within the affected area"): every anchor whose whole `size` footprint lies inside `area`, fits
 * free ({@link deployCellFits}, the SAME primitive the adjacency walls use) and overlaps no section
 * already `placed`. No adjacency to the deployer or between sections; RAW asks for neither. In the
 * area's own cell order, so a prompt's option list is deterministic.
 */
export function legalAreaDeployCells(args: LegalAreaDeployCellsArgs): Cell[] {
  const { state, area, size, placed } = args;
  const world = worldFor(state);
  const areaKeys = new Set(area.map((c) => world.key(c)));
  const taken = new Set(placed.flatMap((p) => world.footprint(p, size).cells().map((c) => world.key(c))));
  return area.filter((anchor) => {
    const cells = world.footprint(anchor, size).cells();
    return cells.every((c) => areaKeys.has(world.key(c)) && !taken.has(world.key(c))) && deployCellFits(state, anchor, size);
  });
}
